// src/components/settings/BlueskyOnboardingWizard.jsx
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { settingsAPI } from '../../services/api';
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    ArrowRightIcon,
    KeyIcon,
    UserIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const STEPS = {
    WELCOME: 'welcome',
    CREDENTIALS: 'credentials',
    VERIFICATION: 'verification',
    COMPLETE: 'complete'
};

const BlueskyOnboardingWizard = ({ onComplete, onClose }) => {
    const [currentStep, setCurrentStep] = useState(STEPS.WELCOME);
    const [credentials, setCredentials] = useState({
        BLUESKY_HANDLE: '',
        BLUESKY_PASSWORD: ''
    });
    const [validationResult, setValidationResult] = useState(null);
    const [isTestingConnection, setIsTestingConnection] = useState(false);

    const queryClient = useQueryClient();

    // Save credentials mutation
    const saveCredentialsMutation = useMutation(
        (creds) => settingsAPI.saveCredentials(creds),
        {
            onSuccess: () => {
                toast.success('Bluesky credentials saved!');
                queryClient.invalidateQueries('credentials');
            },
            onError: (error) => {
                toast.error('Failed to save credentials: ' + error.message);
            }
        }
    );

    // Test connection mutation
    const testConnectionMutation = useMutation(
        () => settingsAPI.testBluesky(),
        {
            onSuccess: (data) => {
                setValidationResult(data);
                setCurrentStep(STEPS.COMPLETE);
                toast.success('Bluesky connection verified!');
            },
            onError: (error) => {
                setValidationResult({ success: false, message: error.message });
                toast.error('Connection test failed: ' + error.message);
            }
        }
    );

    const handleCredentialChange = (key, value) => {
        setCredentials(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSaveAndTest = async () => {
        setIsTestingConnection(true);
        try {
            // Save credentials first
            await saveCredentialsMutation.mutateAsync(credentials);

            // Then test the connection
            await testConnectionMutation.mutateAsync();
        } catch (error) {
            console.error('Failed to save and test:', error);
        } finally {
            setIsTestingConnection(false);
        }
    };

    const isStepComplete = (step) => {
        switch (step) {
            case STEPS.WELCOME:
                return true;
            case STEPS.CREDENTIALS:
                return credentials.BLUESKY_HANDLE && credentials.BLUESKY_PASSWORD;
            case STEPS.VERIFICATION:
                return validationResult?.success;
            default:
                return false;
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case STEPS.WELCOME:
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                                <div className="text-2xl">🦋</div>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                Connect Your Bluesky Account
                            </h2>
                            <p className="text-gray-600 max-w-md mx-auto">
                                Set up Bluesky integration to automatically post your content to the decentralized social network.
                            </p>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">What you'll need:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Your Bluesky handle (username)</li>
                                        <li>Your account password or app password</li>
                                        <li>An active Bluesky account</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <CheckCircleIcon className="h-5 w-5 text-green-400 mt-0.5 mr-3" />
                                <div className="text-sm text-green-800">
                                    <p className="font-medium mb-1">Recommended: Use App Passwords</p>
                                    <p>For better security, create an app password in your Bluesky settings instead of using your main account password.</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-between">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => setCurrentStep(STEPS.CREDENTIALS)}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                            >
                                Get Started
                                <ArrowRightIcon className="ml-2 h-4 w-4" />
                            </button>
                        </div>
                    </div>
                );

            case STEPS.CREDENTIALS:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Enter Your Bluesky Credentials
                            </h2>
                            <p className="text-gray-600">
                                Enter your Bluesky handle and password to connect your account.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Bluesky Handle *
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-gray-500 sm:text-sm">@</span>
                                    </div>
                                    <input
                                        type="text"
                                        value={credentials.BLUESKY_HANDLE}
                                        onChange={(e) => handleCredentialChange('BLUESKY_HANDLE', e.target.value)}
                                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="username.bsky.social"
                                    />
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    Enter your full handle (e.g., username.bsky.social)
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Password or App Password *
                                </label>
                                <input
                                    type="password"
                                    value={credentials.BLUESKY_PASSWORD}
                                    onChange={(e) => handleCredentialChange('BLUESKY_PASSWORD', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Your Bluesky password or app password"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Recommended: Use an app password from Settings → App Passwords
                                </p>
                            </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mt-0.5 mr-3" />
                                <div className="text-sm text-yellow-800">
                                    <p className="font-medium mb-1">Creating an App Password:</p>
                                    <ol className="list-decimal list-inside space-y-1">
                                        <li>Go to Bluesky Settings → App Passwords</li>
                                        <li>Click "Add App Password"</li>
                                        <li>Give it a name (e.g., "Social Media Automation")</li>
                                        <li>Copy the generated password and use it here</li>
                                    </ol>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-between">
                            <button
                                onClick={() => setCurrentStep(STEPS.WELCOME)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Back
                            </button>
                            <button
                                onClick={() => setCurrentStep(STEPS.VERIFICATION)}
                                disabled={!isStepComplete(STEPS.CREDENTIALS)}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
                            >
                                Continue
                                <ArrowRightIcon className="ml-2 h-4 w-4" />
                            </button>
                        </div>
                    </div>
                );

            case STEPS.VERIFICATION:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Test Your Connection
                            </h2>
                            <p className="text-gray-600">
                                Let's verify that your Bluesky credentials are working correctly.
                            </p>
                        </div>

                        {validationResult && !validationResult.success && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mt-0.5 mr-3" />
                                    <div>
                                        <h3 className="text-sm font-medium text-red-800">Connection Failed</h3>
                                        <p className="text-sm text-red-700 mt-1">{validationResult.message}</p>
                                        <div className="mt-2 text-xs text-red-600">
                                            <p>Common issues:</p>
                                            <ul className="list-disc list-inside mt-1">
                                                <li>Incorrect handle format (should include .bsky.social)</li>
                                                <li>Wrong password or app password</li>
                                                <li>Account not found or suspended</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-between">
                            <button
                                onClick={() => setCurrentStep(STEPS.CREDENTIALS)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Back
                            </button>
                            <button
                                onClick={handleSaveAndTest}
                                disabled={isTestingConnection}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 flex items-center"
                            >
                                {isTestingConnection ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Testing...
                                    </>
                                ) : (
                                    <>
                                        Test Connection
                                        <ArrowRightIcon className="ml-2 h-4 w-4" />
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                );

            case STEPS.COMPLETE:
                return (
                    <div className="space-y-6 text-center">
                        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                            <CheckCircleIcon className="h-8 w-8 text-green-600" />
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                Bluesky Connected Successfully!
                            </h2>
                            <p className="text-gray-600">
                                Your Bluesky account is now connected and ready for posting.
                            </p>
                        </div>

                        {validationResult?.data && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="text-sm text-green-800">
                                    <p className="font-medium">Connection Details:</p>
                                    <p>✅ Account authenticated successfully</p>
                                    <p>✅ Ready to post to Bluesky</p>
                                    {validationResult.data.handle && (
                                        <p>📱 Connected as: @{validationResult.data.handle}</p>
                                    )}
                                </div>
                            </div>
                        )}

                        <button
                            onClick={onComplete}
                            className="w-full px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center"
                        >
                            Complete Setup
                            <ArrowRightIcon className="ml-2 h-5 w-5" />
                        </button>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* Background overlay */}
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

                {/* Modal panel */}
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                    {/* Progress bar */}
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                            <h1 className="text-lg font-medium text-gray-900">Bluesky Setup</h1>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="flex items-center space-x-2">
                            {Object.values(STEPS).map((step, index) => (
                                <React.Fragment key={step}>
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                                        currentStep === step
                                            ? 'bg-blue-600 text-white'
                                            : isStepComplete(step)
                                                ? 'bg-green-500 text-white'
                                                : 'bg-gray-200 text-gray-600'
                                    }`}>
                                        {isStepComplete(step) ? '✓' : index + 1}
                                    </div>
                                    {index < Object.values(STEPS).length - 1 && (
                                        <div className={`h-1 w-8 rounded ${
                                            isStepComplete(step) ? 'bg-green-500' : 'bg-gray-200'
                                        }`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="px-6 py-6">
                        {renderStepContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlueskyOnboardingWizard;